import type { CirculoPresetId } from './types';

export const CIRCULO_DIAMETER_FRAC = 0.78;

export const CIRCULO_CENTER = { x: 0.5, y: 0.46 } as const;

/** Pull the subtitle arc this fraction of the way toward the circle rim. */
export const CIRCULO_ARC_TO_RIM = 0.5;

/** Half-width of the smile, as a fraction of composition width. */
const ARC_HALF_SPAN = 0.26;

/** How far the quadratic bows away from the chord, as a fraction of height. */
const ARC_SAGITTA = 0.1;

function circuloArcPoints(
  position: CirculoPresetId,
  width: number,
  height: number,
): { x0: number; y0: number; cx: number; cy: number; x1: number; y1: number } {
  const radius = (CIRCULO_DIAMETER_FRAC * width) / 2;
  const circleY = CIRCULO_CENTER.y * height;
  const originalMid = (position === 'baixo' ? 0.85 : 0.15) * height;
  const rim = position === 'baixo' ? circleY + radius : circleY - radius;
  const apex = originalMid + (rim - originalMid) * CIRCULO_ARC_TO_RIM;
  const sagitta = ARC_SAGITTA * height;
  const x0 = (0.5 - ARC_HALF_SPAN) * width;
  const x1 = (0.5 + ARC_HALF_SPAN) * width;
  const cx = 0.5 * width;
  if (position === 'baixo') {
    return { x0, y0: apex - sagitta, cx, cy: apex + sagitta, x1, y1: apex - sagitta };
  }
  return { x0, y0: apex + sagitta, cx, cy: apex - sagitta, x1, y1: apex + sagitta };
}

export function circuloCircleStyle(
  width: number,
  height: number,
): { left: number; top: number; width: number; height: number } {
  const diameter = width * CIRCULO_DIAMETER_FRAC;
  return {
    left: width * CIRCULO_CENTER.x - diameter / 2,
    top: height * CIRCULO_CENTER.y - diameter / 2,
    width: diameter,
    height: diameter,
  };
}

export function circuloArcPath(
  position: CirculoPresetId,
  width: number,
  height: number,
): string {
  const p = circuloArcPoints(position, width, height);
  return `M ${p.x0} ${p.y0} Q ${p.cx} ${p.cy} ${p.x1} ${p.y1}`;
}

function quadraticPolylineLength(
  x0: number,
  y0: number,
  cx: number,
  cy: number,
  x1: number,
  y1: number,
): number {
  const d1 = Math.hypot(cx - x0, cy - y0);
  const d2 = Math.hypot(x1 - cx, y1 - cy);
  return d1 + d2;
}

export function circuloArcPathLength(
  position: CirculoPresetId,
  width: number,
  height: number,
): number {
  const p = circuloArcPoints(position, width, height);
  return quadraticPolylineLength(p.x0, p.y0, p.cx, p.cy, p.x1, p.y1);
}

export function fitCirculoWordFontSize({
  text,
  maxSize,
  minSize,
  pathLength,
}: {
  text: string;
  fontFamily: string;
  fontWeight: number;
  maxSize: number;
  minSize: number;
  pathLength: number;
}): number {
  const estimated = text.length * maxSize * 0.6;
  if (estimated <= pathLength) {
    return maxSize;
  }
  return Math.max(minSize, maxSize * (pathLength / estimated));
}
